// Wording for the public pages patients open from a text or email (visit hub, email
// preferences, SMS opt-in, ...). The strings live in config/locales/patient_pages and arrive as
// the shared `patientI18n` prop (PatientPageLocale), already in the page's language with English
// underneath, so this module only looks keys up, fills %{placeholders} and picks plural forms.

import { createContext, useContext } from "solid-js";
import { localeDate, localeTime } from "~/util/date/intl_dates";

export type PatientLocaleOption = { code: string; name: string };

type Strings = { [key: string]: string | Strings };

export type PatientI18n = {
  locale: string;
  locales: PatientLocaleOption[];
  // The browser's language, offered in its own words, when the page fell back to another one.
  suggestedLocale: { code: string; prompt: string } | null;
  strings: Strings;
};

export type PatientT = (key: string, vars?: Record<string, string | number>) => string;

// Plural categories differ by language (Ukrainian and Russian use one/few/many/other), so a
// counted key is an object of forms and Intl.PluralRules picks one, falling back to `other`.
export function createPatientT(i18n: PatientI18n): PatientT {
  const plurals = new Intl.PluralRules(i18n.locale);

  return (key, vars = {}) => {
    let entry: string | Strings | undefined = i18n.strings;
    for (const part of key.split(".")) {
      entry = typeof entry === "object" ? entry[camelize(part)] : undefined;
    }

    if (typeof entry === "object" && typeof vars["count"] === "number") {
      entry = entry[plurals.select(vars["count"])] ?? entry["other"];
    }
    if (typeof entry !== "string") return key;

    return entry.replace(/%\{(\w+)\}/g, (match, name: string) =>
      name in vars ? String(vars[name]) : match,
    );
  };
}

// Keys are written as in the locale files ("visit_hub.page_title"); the Inertia prop transformer
// camelizes the hash on the way out ("visitHub.pageTitle").
function camelize(part: string): string {
  return part.replace(/_([a-z\d])/g, (_match, char: string) => char.toUpperCase());
}

// Dates and times in the patient's language, on the office's clock.
export function formatVisitTime(iso: string, timeZone: string, locale: string): string {
  const date = new Date(iso);
  const day = localeDate(locale, date, {
    weekday: "long",
    month: "long",
    day: "numeric",
    timeZone,
  });
  const time = localeTime(locale, date, { hour: "numeric", minute: "2-digit", timeZone });

  // A no-break space keeps the dot with the day when the line wraps.
  return `${capitalize(day, locale)}\u00a0· ${time}`;
}

// "martes, 20 de octubre" reads as a label at the start of a line; Intl leaves it lowercase.
function capitalize(text: string, locale: string): string {
  return text.charAt(0).toLocaleUpperCase(locale) + text.slice(1);
}

// Re-request the page in another language. A full load, so the server sets the locale cookie and
// every later page (the forms a hub hands off to included) opens in the same language.
export function switchPatientLocale(code: string) {
  const url = new URL(window.location.href);
  url.searchParams.set("locale", code);
  // Marks the locale as the patient's own pick (PatientLanguagePick::PARAM), not a link's.
  url.searchParams.set("language_pick", "1");
  window.location.assign(url.toString());
}

// A sentence with a link or other markup in the middle ("Call us at %{phone} with any questions."):
// the text before and after the placeholder, so the page renders its own element between them.
export function splitAround(text: string, placeholder: string): [string, string] {
  const marker = `%{${placeholder}}`;
  const index = text.indexOf(marker);
  if (index === -1) return [text, ""];
  return [text.slice(0, index), text.slice(index + marker.length)];
}

// What a page's subcomponents need: the lookup and the language it formats dates in.
export type PatientText = { t: PatientT; locale: string; i18n: PatientI18n };

export function createPatientText(i18n: PatientI18n): PatientText {
  return { t: createPatientT(i18n), locale: i18n.locale, i18n };
}

const PatientTextContext = createContext<PatientText>();
export const PatientTextProvider = PatientTextContext.Provider;

export function usePatientText(): PatientText {
  const text = useContext(PatientTextContext);
  if (!text) throw new Error("usePatientText must be used within a PatientTextProvider");
  return text;
}
